import { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '@/lib/session';
import {
  listAggregatorOutlets,
  createAggregatorOutlet,
  updateAggregatorOutlet,
  deleteAggregatorOutlet,
  fetchAggregatorRecon,
  type AggregatorOutlet,
  type ChannelRecon,
} from '@/lib/data';

const CHANNELS = ['Swiggy', 'Zomato', 'ONDC'] as const;
const CHANNEL_COLOR: Record<string, string> = { Swiggy: '#F58220', Zomato: '#E23744', ONDC: '#1B6CA8' };

export default function AggregatorsPage() {
  const restaurant = useRestaurant();
  const [tab, setTab] = useState<'mapping' | 'recon'>('mapping');
  const [outlets, setOutlets] = useState<AggregatorOutlet[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ channel: 'Swiggy', externalStoreId: '' });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reconciliation
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: monthAgo, to: today });
  const [recon, setRecon] = useState<ChannelRecon[]>([]);
  const [commissionPct, setCommissionPct] = useState(22);
  const [reconLoading, setReconLoading] = useState(false);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2500); };
  const flashErr = (e: unknown) => { setError(e instanceof Error ? e.message : 'Error'); setTimeout(() => setError(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try { setOutlets(await listAggregatorOutlets(restaurant.id)); }
    catch (e) { flashErr(e); }
    finally { setLoading(false); }
  }, [restaurant.id]);
  useEffect(() => { load(); }, [load]);

  const runRecon = useCallback(async () => {
    setReconLoading(true);
    try {
      const fromISO = new Date(`${range.from}T00:00`).toISOString();
      const toISO = new Date(`${range.to}T23:59:59`).toISOString();
      setRecon(await fetchAggregatorRecon(restaurant.id, fromISO, toISO));
    } catch (e) { flashErr(e); }
    finally { setReconLoading(false); }
  }, [restaurant.id, range]);
  useEffect(() => { if (tab === 'recon') runRecon(); }, [tab, runRecon]);

  const addOutlet = async () => {
    if (!form.externalStoreId.trim()) { flashErr(new Error('Enter the aggregator store ID.')); return; }
    setBusy(true);
    try {
      const created = await createAggregatorOutlet(restaurant.id, form.channel, form.externalStoreId.trim());
      setOutlets((prev) => [...prev, created]);
      setForm({ channel: form.channel, externalStoreId: '' });
      flash('Outlet mapping added.');
    } catch (e) { flashErr(e); }
    finally { setBusy(false); }
  };

  const toggle = async (o: AggregatorOutlet) => {
    try {
      await updateAggregatorOutlet(restaurant.id, o.id, { active: !o.active });
      setOutlets((prev) => prev.map((x) => (x.id === o.id ? { ...x, active: !x.active } : x)));
    } catch (e) { flashErr(e); }
  };

  const remove = async (o: AggregatorOutlet) => {
    if (!window.confirm(`Remove ${o.channel} mapping ${o.externalStoreId}?`)) return;
    try {
      await deleteAggregatorOutlet(restaurant.id, o.id);
      setOutlets((prev) => prev.filter((x) => x.id !== o.id));
    } catch (e) { flashErr(e); }
  };

  const totalGross = recon.reduce((s, r) => s + r.gross, 0);
  const totalOrders = recon.reduce((s, r) => s + r.orders, 0);
  const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground-900">Aggregators</h1>
        <p className="text-sm text-foreground-500">Map your Swiggy / Zomato / ONDC stores and reconcile their orders.</p>
      </div>

      <div className="flex gap-2 border-b border-background-200">
        {([['mapping', 'ri-store-2-line', 'Outlet Mapping'], ['recon', 'ri-scales-3-line', 'Reconciliation']] as const).map(([k, icon, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer ${tab === k ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-500 hover:text-foreground-700'}`}>
            <i className={`${icon} mr-1.5`}></i>{label}
          </button>
        ))}
      </div>

      {toast && <div className="px-4 py-2 text-sm rounded-lg bg-green-100 text-green-700">{toast}</div>}
      {error && <div className="px-4 py-2 text-sm rounded-lg bg-red-100 text-red-700">{error}</div>}

      {tab === 'mapping' ? (
        <>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 max-w-2xl">
            <p className="text-sm font-medium text-foreground-700 mb-3">Add a store mapping</p>
            <p className="text-xs text-foreground-500 mb-3">
              The aggregator sends its own store ID with each order. Map it here so incoming
              orders route to this outlet. The <code>ingest-order</code> function uses these mappings.
            </p>
            <div className="flex flex-wrap gap-3 items-end">
              <label className="block">
                <span className="text-xs font-medium text-foreground-600">Channel</span>
                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500">
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block flex-1 min-w-[180px]">
                <span className="text-xs font-medium text-foreground-600">Aggregator store ID</span>
                <input value={form.externalStoreId} onChange={(e) => setForm({ ...form, externalStoreId: e.target.value })}
                  placeholder="e.g. SWG-OUTLET-12345"
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <button onClick={addOutlet} disabled={busy}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50">
                {busy ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>

          {loading ? <p className="text-sm text-foreground-500">Loading…</p>
            : outlets.length === 0 ? (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-store-2-line text-4xl"></i>
                <p className="mt-2 text-sm">No store mappings yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-background-200 max-w-2xl">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100 text-left text-foreground-600">
                    <th className="p-3 font-medium">Channel</th><th className="p-3 font-medium">Store ID</th>
                    <th className="p-3 font-medium">Active</th><th className="p-3 font-medium text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {outlets.map((o) => (
                      <tr key={o.id} className="border-t border-background-100">
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium text-background-50" style={{ backgroundColor: CHANNEL_COLOR[o.channel] ?? '#666' }}>{o.channel}</span>
                        </td>
                        <td className="p-3 font-mono text-xs text-foreground-700">{o.externalStoreId}</td>
                        <td className="p-3">
                          <button onClick={() => toggle(o)} className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${o.active ? 'bg-primary-500' : 'bg-background-300'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${o.active ? 'translate-x-4' : 'translate-x-0.5'}`}></span>
                          </button>
                        </td>
                        <td className="p-3 text-right">
                          <button onClick={() => remove(o)} className="text-foreground-400 hover:text-red-600 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 items-end bg-background-50 border border-background-200 rounded-lg p-4 max-w-2xl">
            <label className="block"><span className="text-xs font-medium text-foreground-600">From</span>
              <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
            <label className="block"><span className="text-xs font-medium text-foreground-600">To</span>
              <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
            <label className="block"><span className="text-xs font-medium text-foreground-600">Commission %</span>
              <input type="number" min={0} max={100} value={commissionPct} onChange={(e) => setCommissionPct(Number(e.target.value))}
                className="mt-1 w-24 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
            <button onClick={runRecon} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer">Refresh</button>
          </div>

          {reconLoading ? <p className="text-sm text-foreground-500">Calculating…</p> : (
            <div className="overflow-x-auto rounded-lg border border-background-200 max-w-2xl">
              <table className="w-full text-sm">
                <thead><tr className="bg-background-100 text-left text-foreground-600">
                  <th className="p-3 font-medium">Channel</th><th className="p-3 font-medium text-right">Orders</th>
                  <th className="p-3 font-medium text-right">Gross</th>
                  <th className="p-3 font-medium text-right">Est. commission</th>
                  <th className="p-3 font-medium text-right">Est. payout</th>
                </tr></thead>
                <tbody>
                  {recon.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-foreground-400">No aggregator orders in this period.</td></tr>
                  ) : recon.map((r) => {
                    const comm = r.gross * commissionPct / 100;
                    return (
                      <tr key={r.channel} className="border-t border-background-100">
                        <td className="p-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium text-background-50" style={{ backgroundColor: CHANNEL_COLOR[r.channel] ?? '#666' }}>{r.channel}</span></td>
                        <td className="p-3 text-right text-foreground-700">{r.orders}</td>
                        <td className="p-3 text-right text-foreground-800">{inr(r.gross)}</td>
                        <td className="p-3 text-right text-red-600">-{inr(comm)}</td>
                        <td className="p-3 text-right font-medium text-foreground-900">{inr(r.gross - comm)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {recon.length > 0 && (
                  <tfoot><tr className="border-t border-background-200 bg-background-100 font-medium">
                    <td className="p-3">Total</td><td className="p-3 text-right">{totalOrders}</td>
                    <td className="p-3 text-right">{inr(totalGross)}</td>
                    <td className="p-3 text-right text-red-600">-{inr(totalGross * commissionPct / 100)}</td>
                    <td className="p-3 text-right">{inr(totalGross * (1 - commissionPct / 100))}</td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          )}
          <p className="text-xs text-foreground-500 max-w-2xl">
            Compare the gross + order count above against the aggregator's settlement report to spot
            missing or mismatched payouts. Commission % is an estimate you set per your contract.
          </p>
        </>
      )}
    </div>
  );
}
